import React, { useState, useEffect } from "react";
import { FaTemperatureHigh, FaTint, FaDesktop } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import useZoneData from "./useZonedata";
import { Hand, ArrowUpDown } from "lucide-react";
import WeatherAirQuality from "./WeatherAirQuality";
import { useSearchParams } from 'react-router-dom';
import {
  FloorLevel,
  getFacilityFloor,
  mapCategoryToFacility,
  SPECIAL_FACILITY_URLS,
} from "./facilityUtils";

interface SubItem {
  name: string;
  
}

interface Subcategory {
  name: string;
  subItems?: SubItem[];
  
}

interface Category {
  name: string;
  subcategories: Subcategory[];
  
}

interface APISubItem {
  name: string;
  id: string;
  query_id?: string;
  description?: string;
  image?: string;
  section_title_l3?: string; 
  
}

interface APISubcategory {
  name: string;
  id: string;
  section_title_l2?: string; 
  subItems?: APISubItem[];
  query_id?: string; 
}

interface APICategory {
  name: string;
  query_id?: string; 
  id: string;
  subcategories: APISubcategory[];
}

const useFacilityData = () => {
  const [facilities, setFacilities] = useState<APICategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchFacilities = async () => {
      try {
        setLoading(true);
        
        // Fetch first levels only
        const response = await fetch(
          "https://facility-search-demo-dot-optimus-lnu.df.r.appspot.com/updated/api/library_facilities/"
        );
        const data = await response.json();
        
        // Now need to fetch children for each first level to get complete data
        const firstLevels = data.data || [];
        
        // Fetch all children in parallel
        const facilitiesWithChildren = await Promise.all(
          firstLevels.map(async (firstLevel: any) => {
            try {
              const childrenResponse = await fetch(
                `https://facility-search-demo-dot-optimus-lnu.df.r.appspot.com/updated/api/library_facilities/children/${firstLevel.id}`
              );
              const childrenData = await childrenResponse.json();
              
              return {
                ...firstLevel,
                second_levels: childrenData.second_levels || []
              };
            } catch (err) {
              console.error(`Failed to fetch children for ${firstLevel.id}:`, err);
              return {
                ...firstLevel,
                second_levels: []
              };
            }
          })
        );
        
        const transformedData = transformAPIData(facilitiesWithChildren);
        setFacilities(transformedData);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to fetch facilities"
        );
        setFacilities([]);
      } finally {
        setLoading(false);
      }
    };
    fetchFacilities();
  }, []);

  return { facilities, loading, error };
};

const transformAPIData = (apiData: any[]): APICategory[] => {
  return apiData
    .filter((item) => !item.deleted)
    .map((firstLevel) => ({
      name: firstLevel.name,
      id: firstLevel.id,
      query_id: firstLevel.query_id,
      subcategories:
        firstLevel.second_levels
          ?.filter((item: any) => !item.deleted)
          ?.map((secondLevel: any) => ({
            name: secondLevel.name,
            id: secondLevel.id,
            query_id: secondLevel.query_id,
            section_title_l2: secondLevel.section_title_l2,
            subItems:
              secondLevel.third_levels
                ?.filter((item: any) => !item.deleted)
                ?.map((thirdLevel: any) => ({
                  name: thirdLevel.name,
                  id: thirdLevel.id,
                  query_id: thirdLevel.query_id,
                  description: thirdLevel.description,
                  image: thirdLevel.image,
                  section_title_l3: thirdLevel.section_title_l3, 
                })) || [],
          })) || [],
    }));
};

type OccupancyStatus = "available" | "lessAvailable" | "crowded";

const ThirdFloorViewer: React.FC = () => {
  const navigate = useNavigate();
  const [specialMapUrl, setSpecialMapUrl] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
      const [isPanelCollapsed, setIsPanelCollapsed] = useState<boolean>(false);
  
  const selectedLevel: FloorLevel = "3/F"; // Always 3/F for this component
  const [searchParams] = useSearchParams();
const [queryId, setQueryId] = useState<string>("");
  const [expandedCategories, setExpandedCategories] = useState<
    Record<string, boolean>
  >({});
  const [expandedSubcategories, setExpandedSubcategories] = useState<
    Record<string, boolean>
  >({});
  const [facility, setFacility] = useState<string>("");
  const [openingHours, setOpeningHours] = useState<string>("Loading...");
  const [isDisplayToggled, setIsDisplayToggled] = useState<boolean>(() => {
    const displayParam = new URLSearchParams(window.location.search).get("display");
    return displayParam === "toggled";
  });  const {
    facilities: apiFacilities,
    loading: facilitiesLoading,
    error: facilitiesError,
  } = useFacilityData();

  // data hook
  const { zoneData, floorAverages, crData, mprData, cpr2Data, loading, error } =
    useZoneData();

    const handleSpecialFacilities = (facilityName: string): boolean => {
      const url = SPECIAL_FACILITY_URLS[facilityName];
      if (url) {
        setSpecialMapUrl(url);
        setFacility(mapCategoryToFacility(facilityName));
        setTimeout(() => {
          const iframe = document.getElementById('iframe') as HTMLIFrameElement;
          if (iframe && iframe.contentWindow) {
            iframe.contentWindow.postMessage({
              type: 'SHOW_TOOLTIP',
              facilityName: mapCategoryToFacility(facilityName)
            }, '*');
          }
        }, 3000);
        return true;
      }
      return false;
    };

    
  // SVG icons
  const availableIcon = (
    <svg
      width="48"
      height="32"
      viewBox="0 0 70 40"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g>
        <circle cx="15" cy="10" r="7" fill="#4ade80" />
        <path d="M7,20 Q15,13 23,20 L23,32 Q15,36 7,32 Z" fill="#4ade80" />
        <circle
          cx="35"
          cy="8"
          r="7"
          stroke="#4ade80"
          strokeWidth="1.5"
          fill="none"
        />
        <path
          d="M27,18 Q35,11 43,18 L43,30 Q35,34 27,30 Z"
          stroke="#4ade80"
          strokeWidth="1.5"
          fill="none"
        />
        <circle
          cx="55"
          cy="10"
          r="7"
          stroke="#4ade80"
          strokeWidth="1.5"
          fill="none"
        />
        <path
          d="M47,20 Q55,13 63,20 L63,32 Q55,36 47,32 Z"
          stroke="#4ade80"
          strokeWidth="1.5"
          fill="none"
        />
      </g>
    </svg>
  );

  const lessAvailableIcon = (
    <svg
      width="48"
      height="32"
      viewBox="0 0 70 40"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g>
        <circle cx="15" cy="10" r="7" fill="#fbbf24" />
        <path d="M7,20 Q15,13 23,20 L23,32 Q15,36 7,32 Z" fill="#fbbf24" />
        <circle cx="35" cy="8" r="7" fill="#fbbf24" />
        <path d="M27,18 Q35,11 43,18 L43,30 Q35,34 27,30 Z" fill="#fbbf24" />
        <circle
          cx="55"
          cy="10"
          r="7"
          stroke="#fbbf24"
          strokeWidth="1.5"
          fill="none"
        />
        <path
          d="M47,20 Q55,13 63,20 L63,32 Q55,36 47,32 Z"
          stroke="#fbbf24"
          strokeWidth="1.5"
          fill="none"
        />
      </g>
    </svg>
  );

  const crowdedIcon = (
    <svg
      width="48"
      height="32"
      viewBox="0 0 70 40"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g>
        <circle cx="15" cy="10" r="7" fill="#ef4444" />
        <path d="M7,20 Q15,13 23,20 L23,32 Q15,36 7,32 Z" fill="#ef4444" />
        <circle cx="35" cy="8" r="7" fill="#ef4444" />
        <path d="M27,18 Q35,11 43,18 L43,30 Q35,34 27,30 Z" fill="#ef4444" />
        <circle cx="55" cy="10" r="7" fill="#ef4444" />
        <path d="M47,20 Q55,13 63,20 L63,32 Q55,36 47,32 Z" fill="#ef4444" />
      </g>
    </svg>
  );

  // Floor map URL for 3/F only
  const thirdFloorMapUrl =
    "https://pwwpdev.github.io/Lingnan/3f_facility_general.html";

  // Navigation function for different floors
  const navigateToFloor = (level: FloorLevel): void => {
    const params = new URLSearchParams();
  if (isDisplayToggled) params.append('display', 'toggled');
  
  const queryString = params.toString();
  const suffix = queryString ? `?${queryString}` : '';

    switch (level) {
      case "1/F":
        navigate(`/first-floor${suffix}`);
        break;
      case "M/F":
        navigate(`/m-floor${suffix}`); 
        break;
      case "2/F":
        navigate(`/second-floor${suffix}`);
        break;
      case "3/F":
        // Already on 3/F, do nothing
        break;
      default:
        break;
    }
  };

  const fetchOpeningHours = async () => {
    try {
      const response = await fetch(
        "https://lnudevices-dot-optimus-hk.df.r.appspot.com/library-opening-hours",
        {
          mode: "cors",
        }
      );
      const html = await response.text();

      // Create a temporary DOM element to parse the HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");

      // Find the opening hours content
      const contentDiv = doc.querySelector(".floor-plan-opening-hour .content");
      if (contentDiv) {
        let hours = contentDiv.textContent?.trim() || "Hours not available";
        // Clean up any HTML entities like &nbsp;
        hours = hours.replace(/\u00A0/g, " "); // Replace non-breaking spaces
        setOpeningHours(hours);
      } else {
        setOpeningHours("Hours not available");
      }
    } catch (error) {
      console.error("Error fetching opening hours:", error);
      setOpeningHours("8:30 a.m. - 9:00 p.m. (Overnight Study Area Open)"); // Fallback
    }
  };

  // categories and subcategories for the multilevel dropdown
  const [categories, setCategories] = useState([
    {
      name: "Service Counter (1/F Zone A)",
      subcategories: [],
    },
    {
      name: "Collections",
      subcategories: [
        {
          name: "Books A-Z",
          subItems: [
            { name: "Books A-DS776 (1/F Zone A)" },
            { name: "Books DS777-HJ (1/F Zone C)" },
            { name: "Books HM-HM1100 (M/F Zone B)" },
            { name: "Books HM1101-HN (2/F Zone B)" },
            { name: "Books HN-HS (3/F Zone A)" },
            { name: "Books HT-NX (3/F Zone B)" },
            { name: "Books P-ZA (3/F Zone C)" },
          ],
        },
        {
          name: "Course Reserves (1/F Zone A)",
          subItems: [
            { name: "Good Reads, New Arrivals (1/F Zone B)" },
            { name: "High Use Books (1/F Zone B)" },
          ],
        },
        {
          name: "Journals [SER] (2/F Zone A)",
          subItems: [
            { name: "Current Journals (2/F Zone A)" },
            { name: "Bound Journals (2/F Zone A)" },
          ],
        },
        {
          name: "Language Examinations & Learning Resources [LANG] (2/F Zone C)",
        },
        { name: "Large Books [LBK], Extra Large Books [XLBK] (3/F Zone A)" },
        { name: "Lingnan University Archives (2/F Zone A)" },
        {
          name: "Multimedia [AVM]",
          subItems: [
            { name: "Multimedia (2/F Zone C)" },
            {
              name: "Category III Multimedia – Ask at Service Counter (1/F Zone A)",
            },
          ],
        },
        {
          name: "Newspapers",
          subItems: [
            { name: "Current Issues (2/F Zone B)" },
            { name: "Back Issues (2/F Zone A)" },
          ],
        },
        {
          name: "References [REF]",
          subItems: [
            { name: "References A-HM (2/F Zone A)" },
            { name: "References HN-Z (2/F Zone C)" },
          ],
        },
        { name: "Staff Publications (1/F Zone B)" },
      ],
    },
    {
      name: "Computers",
      subcategories: [
        { name: "Chiang Chen Information Commons (1/F Zone A)" },
        { name: "Computer Rooms (1/F Zone B)" },
        {
          name: "Lee Hak Kan Multimedia and Language Learning Centre – MLLC (2/F Zone C)",
        },
        {
          name: "SEN Workstations",
          subItems: [
            { name: "SEN Workstation 1 (1/F Zone B)" },
            { name: "SEN Workstation 2 (1/F Zone C)" },
          ],
        },
        { name: "Bloomberg Terminal – Database Workstation (1/F Zone B)" },
        { name: "LSEG Workspace (1/F Zone B)" },
        { name: "WIND Terminal (1/F Zone B)" },
      ],
    },
    {
      name: "MultiFunction Printers",
      subcategories: [
        {
          name: "Copiers/Printers/Scanners (Section Title – for display only)",
        },
        { name: "11-13 (1/F Zone A)" },
        { name: "14-16BW (1/F Zone B)" },
        { name: "17 (1/F Zone C)" },
        { name: "21, 23 (2/F Zone C)" },
        { name: "22BW (2/F Zone A)" },
        { name: "31BW-32 (3/F Zone B)" },
      ],
    },
    {
      name: "Study Rooms",
      subcategories: [
        { name: "Individual Study Rooms (Section Title – for display only)" },
        { name: "Individual Study Rooms 1 – 7 (1/F Zone C)" },
        { name: "Individual Study Rooms 8 (1/F Zone C)" },
        { name: "Individual Study Rooms 9 – 11 (1/F Zone A)" },
        { name: "Group Study Rooms (Section Title – for display only)" },
        { name: "Group Study Rooms 1 – 4 (1/F Zone C)" },
        { name: "Group Study Room 5 (1/F Zone C)" },
        { name: "Group Study Rooms 8 – 9 (1/F Zone A)" },
        { name: "Group Study Room 10 – 11 (2/F Zone B)" },
      ],
    },
    {
      name: "Quiet Study Spaces",
      subcategories: [
        { name: "1/F Study Area (Section Title – for display only)" },
        { name: "Late Reading Room (1/F Zone B)" },
        { name: "Postgraduate Research Carrels (1/F Zone B)" },
        { name: "Individual Study Carrels (1/F Zone A & Zone C)" },
        { name: "M/F Study Area (Section Title – for display only)" },
        { name: "Individual Study Carrels (M/F Zone C)" },
        { name: "Postgraduate Lounge (M/F Zone C)" },
        { name: "2/F Study Area (Section Title – for display only)" },
        { name: "Chang Han-tsiu Reading Room (2/F Zone A)" },
        { name: "3/F Study Area (Section Title – for display only)" },
        { name: "Multi-Purpose Room 1 (3/F Zone C)" },
        { name: "Multi-Purpose Room 2 (3/F Zone A)" },
      ],
    },
    {
      name: "Group Study Spaces",
      subcategories: [
        { name: "1/F Study Area (Section Title – for display only)" },
        { name: "Activity Room (1/F Zone A)" },
        { name: "Café (1/F Zone A)" },
        { name: "Computer Room 1 & 2 (1/F Zone B)" },
        { name: "M/F Study Area (Section Title – for display only)" },
        { name: "LU Barn (Makerspace) (M/F Zone A)" },
        { name: "2/F Study Area (Section Title – for display only)" },
        {
          name: "Lee Hak Kan Multimedia and Language Learning Centre – MLLC (2/F Zone C)",
        },
        { name: "Mini Theatre (2/F Zone B)" },
      ],
    },
    {
      name: "Overnight Study Area",
      subcategories: [
        { name: "Late Reading Room (1/F Zone B)" },
        { name: "Computer Room 1 & 2 (1/F Zone B)" },
      ],
    },
    {
      name: "Drinking Water Stations",
      subcategories: [
        { name: "1/F (1/F Zone B)" },
        { name: "2/F (2/F Zone A)" },
        { name: "3/F (3/F Zone A & Zone C)" },
      ],
    },
    {
      name: "Toilets",
      subcategories: [
        { name: "1/F Toilets (Section Title – for display only)" },
        { name: "1/F Male & Female (Zone A & Zone C)" },
        {
          name: "1/F Unisex – Available during Overnight Study Period (Zone B)",
        },
        { name: "1/F Accessible Toilets (Zone A & Zone C)" },
        { name: "2/F Toilets (Section Title – for display only)" },
        { name: "2/F Male & Female (Zone A & Zone C)" },
        { name: "3/F Toilets (Section Title – for display only)" },
        { name: "3/F Male & Female (Zone A & Zone C)" },
        { name: "3/F Accessible Toilets (Zone C)" },
      ],
    },
    {
      name: "Other Facilities",
      subcategories: [
        { name: "Book Drops (1/F Zone B)" },
        { name: "Book Sterilizer (1/F Zone B)" },
        { name: "Self-Checkout Machine (1/F Zone A & Zone B)" },
        { name: "Self-Pickup Locker (1/F Zone B)" },
        { name: "SEN Equipment – Service Counter (1/F Zone B)" },
        {
          name: "LU Barn (Makerspace) Equipment",
          subItems: [
            { name: "3D Printers – LU Barn (Makerspace) (M/F Zone A)" },
            { name: "3D Scanner – LU Barn (Makerspace) (M/F Zone A)" },
            {
              name: "Laser Cutter / Engraver – LU Barn (Makerspace) Maker Room (1/F Zone A)",
            },
          ],
        },
        { name: "Lift (1/F – 3/F Zone B)" },
      ],
    },
  ]);

  const [filteredCategories, setFilteredCategories] =
    useState<Category[]>(categories);

  // filter categories based on search term
  useEffect(() => {
    if (!searchTerm) {
      setFilteredCategories(categories);
      return;
    }
  
    const filtered = categories.filter((category: Category) => {
      // check if category name matches
      if ((category.name || "").toLowerCase().includes(searchTerm.toLowerCase())) {
        return true;
      }
  
      // check if any subcategory matches
      if (category.subcategories && category.subcategories.length > 0) {
        const hasMatchingSubcategory = category.subcategories.some(
          (subcategory: Subcategory) => {
            if (
              (subcategory.name || "").toLowerCase().includes(searchTerm.toLowerCase())
            ) {
              return true;
            }
  
            // check for sub-items matches
            if (subcategory.subItems && subcategory.subItems.length > 0) {
              return subcategory.subItems.some((subItem: SubItem) =>
                (subItem.name || "").toLowerCase().includes(searchTerm.toLowerCase())
              );
            }
  
            return false;
          }
        );
        return hasMatchingSubcategory;
      }
  
      return false;
    });
  
    setFilteredCategories(filtered);
  
    // Auto-expand matching categories and subcategories
    if (searchTerm) {
      const newExpandedCategories: Record<string, boolean> = {};
      const newExpandedSubcategories: Record<string, boolean> = {};
  
      filtered.forEach((category) => {
        // Expand category if it has subcategories and contains matches
        if (category.subcategories && category.subcategories.length > 0) {
          newExpandedCategories[category.name] = true;
  
          // Check subcategories for matches
          category.subcategories.forEach((subcategory) => {
            // Expand subcategory if it has sub-items and contains matches
            if (subcategory.subItems && subcategory.subItems.length > 0) {
              const hasMatchingSubItem = subcategory.subItems.some((subItem) =>
                (subItem.name || "").toLowerCase().includes(searchTerm.toLowerCase())
              );
              if (hasMatchingSubItem || (subcategory.name || "").toLowerCase().includes(searchTerm.toLowerCase())) {
                newExpandedSubcategories[subcategory.name] = true;
              }
            }
          });
        }
      });
  
      setExpandedCategories(newExpandedCategories);
      setExpandedSubcategories(newExpandedSubcategories);
    }
  }, [searchTerm, categories]);

  useEffect(() => {
    const facilityParam = searchParams.get("facility") ? decodeURIComponent(searchParams.get("facility")!) : null;
    const queryParam = searchParams.get("query");
    const fromParam = searchParams.get("from");
    const displayParam = searchParams.get("display"); 
  
    console.log("URL Params:", { facilityParam, queryParam, fromParam });
  
    // IMPORTANT: Set queryId first
    if (queryParam) {
      setQueryId(queryParam);
      console.log("Setting query ID:", queryParam);
    }
  
    if (queryParam && apiFacilities.length > 0) {
      // Search for facility by query_id in API data
      let foundFacility = null;
      
      for (const firstLevel of apiFacilities) {
        // Check first level
        if (firstLevel.query_id === queryParam) {
          foundFacility = firstLevel.name;
          console.log("Found at first level:", foundFacility);
          break;
        }
        
        for (const secondLevel of firstLevel.subcategories || []) {
          // Check second level
          if (secondLevel.query_id === queryParam) {
            foundFacility = secondLevel.name;
            console.log("Found at second level:", foundFacility);
            break;
          }
          
          // Check third level
          const apiSubItem = secondLevel.subItems?.find(item => item.query_id === queryParam);
          if (apiSubItem) {
            foundFacility = apiSubItem.name;
            console.log("Found at third level:", foundFacility);
            break;
          }
        }
        if (foundFacility) break;
      }
      
      if (foundFacility) {
        console.log("Found facility by query_id:", foundFacility);
        
        // CHECK IF FACILITY IS ON A DIFFERENT FLOOR
        const facilityFloor = getFacilityFloor(queryParam);
        const currentFloor = "3/F"; 
        
        console.log("Query param facility floor:", facilityFloor, "Current floor:", currentFloor);
        
        if (facilityFloor && facilityFloor !== currentFloor) {
          // Facility is on a different floor - redirect!
          console.log("Redirecting to correct floor:", facilityFloor);
          const params = new URLSearchParams();
          params.append('facility', encodeURIComponent(mapCategoryToFacility(foundFacility)));
          params.append('query', queryParam);
          if (isDisplayToggled) params.append('display', 'toggled');
          
          let floorRoute = "";
          switch (facilityFloor) {
            case "M/F":
              floorRoute = "m";
              break;
            case "2/F":
              floorRoute = "second";
              break;
            case "1/F":
              floorRoute = "first";
              break;
            default:
              floorRoute = "third";
          }
          
          navigate(`/${floorRoute}-floor?${params.toString()}`, { replace: true });
        } else {
          // Facility is on current floor - handle it here
          if (!handleSpecialFacilities(foundFacility)) {
            setSpecialMapUrl(null);
            const mappedFacility = mapCategoryToFacility(foundFacility);
            setFacility(mappedFacility);
          }
        }
      } else {
        console.log("No facility found for query_id:", queryParam);
      }
    } else if (facilityParam) {
      setFacility(facilityParam);
    }
    
    if (displayParam === "toggled") {
      setIsDisplayToggled(true);
    }
  }, [searchParams, navigate, apiFacilities]);

  useEffect(() => {
    if (apiFacilities && apiFacilities.length > 0) {
      const transformedCategories: Category[] = apiFacilities.map((apiCat) => ({
        name: apiCat.name,
        subcategories: apiCat.subcategories.map((apiSub) => ({
          name: apiSub.name,
          subItems:
            apiSub.subItems?.map((apiSubItem) => ({
              name: apiSubItem.name,
            })) || [],
        })),
      }));

      setCategories(transformedCategories);
      console.log("Updated categories from API:", transformedCategories);
    }
  }, [apiFacilities]);

  // Fetch opening hours on component mount and every hour
  useEffect(() => {
    // Fetch immediately
    fetchOpeningHours();

    // Set up interval to fetch every hour (3600000 milliseconds)
    const hoursInterval = setInterval(fetchOpeningHours, 3600000);

    // Cleanup interval on component unmount
    return () => {
      if (hoursInterval) {
        clearInterval(hoursInterval);
      }
    };
  }, []);

 // toggle expansion of category
const toggleCategory = (categoryName: string): void => {
  if (handleSpecialFacilities(categoryName)) {
    return; // Exit early if this was a special facility
  }
  setSpecialMapUrl(null); // Reset special map URL
  // Set facility and reload map if category has no subcategories
  const category = categories.find((cat) => cat.name === categoryName);
  if (!category?.subcategories || category.subcategories.length === 0) {
    // Check if facility is on a different floor
    const facilityFloor = getFacilityFloor(mapCategoryToFacility(categoryName))
;
    
    if (facilityFloor && facilityFloor !== "3/F") {
      // Navigate to the correct floor with facility parameter
      const params = new URLSearchParams();
      params.append('facility', encodeURIComponent(mapCategoryToFacility(categoryName)));
      params.append('query', mapCategoryToFacility(categoryName));
      params.append('from', '3/F');
      if (isDisplayToggled) params.append('display', 'toggled');
      
      let floorRoute = "";
      switch (facilityFloor) {
        case "1/F":
          floorRoute = "first";
          break;
        case "M/F":
          floorRoute = "m";
          break;
        case "2/F":
          floorRoute = "second";
          break;
        default:
          floorRoute = "third";
      }
      
      navigate(`/${floorRoute}-floor?${params.toString()}`);
    } else {
      // Handle on current floor
      const mappedFacility = mapCategoryToFacility(categoryName);
      setFacility(mappedFacility);
      console.log("Original category:", categoryName);
      console.log("Mapped facility:", mappedFacility);
    }
  }

  // Accordion behavior: close all other categories and toggle current one
  setExpandedCategories((prev) => {
    const isCurrentlyExpanded = prev[categoryName];

    // Create new state with all categories closed
    const newState: Record<string, boolean> = {};
    Object.keys(prev).forEach((key) => {
      newState[key] = false;
    });

    // If the current category was closed, open it; if it was open, keep it closed
    newState[categoryName] = !isCurrentlyExpanded;

    return newState;
  });

  // Also close all subcategories when switching between main categories
  if (category?.subcategories && category.subcategories.length > 0) {
    setExpandedSubcategories({});
  }
};

  // toggle expansion of subcategory
  const toggleSubcategory = (subcategoryName: string): void => {
    // Accordion behavior: close all other subcategories and toggle current one
    setExpandedSubcategories((prev) => {
      const isCurrentlyExpanded = prev[subcategoryName];

      // Create new state with all subcategories closed
      const newState: Record<string, boolean> = {};
      Object.keys(prev).forEach((key) => {
        newState[key] = false;
      });

      // If the current subcategory was closed, open it; if it was open, keep it closed
      newState[subcategoryName] = !isCurrentlyExpanded;

      return newState;
    });
  };

  // helper function to render occupancy icon based on status
  const renderOccupancyIcon = (status: OccupancyStatus): JSX.Element => {
    switch (status) {
      case "available":
        return availableIcon;
      case "lessAvailable":
        return lessAvailableIcon;
      case "crowded":
        return crowdedIcon;
      default:
        return availableIcon;
    }
  };

  // helper function to render CO2 level icon based on ppm
  const renderCO2StatusIcon = (ppm: number): JSX.Element => {
    if (ppm < 800) {
      return (
        <svg
          width="38"
          height="38"
          viewBox="0 0 24 24"
          fill="#4ade80"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle
            cx="12"
            cy="12"
            r="10"
            stroke="#4ade80"
            strokeWidth="2"
            fill="none"
          />
          <path
            d="M7,13 C9,17 15,17 17,13"
            stroke="#4ade80"
            strokeWidth="2"
            strokeLinecap="round"
            fill="none"
          />
          <circle cx="8" cy="9" r="1.5" fill="#4ade80" />
          <circle cx="16" cy="9" r="1.5" fill="#4ade80" />
        </svg>
      );
    } else if (ppm < 1500) {
      return (
        <svg
          width="38"
          height="38"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle
            cx="12"
            cy="12"
            r="10"
            stroke="#fbbf24"
            strokeWidth="2"
            fill="none"
          />
          <path d="M8,14 L16,14" stroke="#fbbf24" strokeWidth="2" />
          <circle cx="8" cy="9" r="1" fill="#fbbf24" />
          <circle cx="16" cy="9" r="1" fill="#fbbf24" />
        </svg>
      );
    } else {
      return (
        <svg
          width="38"
          height="38"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle
            cx="12"
            cy="12"
            r="10"
            stroke="#ef4444"
            strokeWidth="2"
            fill="none"
          />
          <path
            d="M8,16 C8,14 16,14 16,16"
            stroke="#ef4444"
            strokeWidth="2"
            fill="none"
          />
          <circle cx="8" cy="9" r="1" fill="#ef4444" />
          <circle cx="16" cy="9" r="1" fill="#ef4444" />
        </svg>
      );
    }
  };

  // helper function to determine overall floor occupancy
  const getFloorOccupancy = (floor: string): OccupancyStatus => {
    if (!zoneData[floor]) return "available";

    const zones = Object.keys(zoneData[floor]);
    if (zones.length === 0) return "available";

    const occupancyCount = {
      available: 0,
      lessAvailable: 0,
      crowded: 0,
    };

    zones.forEach((zone) => {
      occupancyCount[zoneData[floor][zone].occupancy]++;
    });

    if (occupancyCount.crowded > 0) return "crowded";
    if (occupancyCount.lessAvailable > occupancyCount.available)
      return "lessAvailable";
    return "available";
  };

  // helper function to render floor occupancy icon
  const renderFloorOccupancyIcon = (floor: string): JSX.Element => {
    const occupancy = getFloorOccupancy(floor);
    return renderOccupancyIcon(occupancy);
  };

  const getCurrentDate = (): string => {
    const days = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    const now = new Date();
    const day = now.getDate();
    const month = months[now.getMonth()];
    const year = now.getFullYear();
    const weekday = days[now.getDay()];

    return `${day} ${month} ${year} ${weekday}`;
  };

  return (
    <div className="flex flex-col h-screen">


  
      {/* header */}
      <header className="bg-white custom-shadow h-20 xl:h-[100px] lg:h-[140px] w-full z-10 flex items-center justify-between md:px-4 px-2">
        <div className="flex items-center h-full">
          <img
            src="/library-logo-final_2024.png"
            alt="Lingnan University Fong Sum Wood Library"
            className="h-8 lg:h-[59px] xl:h-[58px] ml-1"
          />
        </div>

        <div className=" flex md:flex-row flex-col items-center">
        <div className="sm:text-[18px] md:pr-4 flex justify-end text-gray-600 text-[10px] font-medium"> {getCurrentDate()}</div>
         <WeatherAirQuality className="text-[10px] md:text-xl sm:text-lg flex justify-end lg:text-[22px]" />
        </div>
      </header>

      {/* banner */}
      {/* <div className="bg-white p-3 flex justify-between items-center border-b">
        <div className="text-gray-600 font-semibold text-sm lg:text-xl">
          <span className="font-bold text-teal-600">
            Today's Opening Hours:
          </span>{" "}
          <span className="lg:text-2xl text-sm">{openingHours}</span>
        </div>
      </div> */}



      {/* floor selector */}
      <div className="block p-3 bg-white custom-shadow">
      <div className="flex w-full justify-evenly lg:justify-start lg:space-x-3">
      <button
  onClick={() => navigateToFloor("1/F")}
  className={`lg:px-10 lg:py-4 px-8 py-2 rounded-full md:text-2xl transition-all duration-200 ${
    specialMapUrl?.includes("lrr")
      ? "bg-[#440099] text-white font-semibold"
      : "bg-gray-200 text-[#545454] hover:bg-gray-300"
  }`}
>
  1/F
</button>
<button
  onClick={() => navigateToFloor("M/F")}
  className={`lg:px-10 lg:py-4 px-8 py-2 rounded-full md:text-2xl transition-all duration-200 ${
    specialMapUrl?.includes("mfc")
      ? "bg-[#5cb8b2] text-white font-semibold"
      : "bg-gray-200 text-[#545454] hover:bg-gray-300"
  }`}
>
  M/F
</button>
<button
  onClick={() => navigateToFloor("2/F")}
  className="lg:px-10 lg:py-4 px-8 py-2 rounded-full md:text-2xl transition-all duration-200 bg-gray-200 text-[#545454] hover:bg-gray-300"
>
  2/F
</button>
<button
  onClick={() => navigateToFloor("3/F")}
  className={`lg:px-10 lg:py-4 px-8 py-2 rounded-full md:text-2xl transition-all duration-200 ${
    specialMapUrl
      ? "bg-gray-200 text-[#545454] hover:bg-gray-300"
      : "bg-[#ce0058] text-white"
  }`}
>
  3/F
</button>
        </div>
      </div>

      {/* main content - map and search facility */}
      <div className="flex flex-col md:flex-row w-full md:h-[50vh]">
        {/* 3d Viewer (75% width) - 3/F Map Only */}
        <div
  className={`${
    isPanelCollapsed ? "md:w-full" : "md:w-3/4"
  } w-full h-[50vh] relative`}
>            
<iframe
  id="iframe"
  src={(() => {
    // If there's a special map URL, use it directly
    if (specialMapUrl) {
      return specialMapUrl;
    }
    
    // Otherwise, use the regular URL construction logic
    const params = new URLSearchParams();
    if (facility) params.append('facility', facility);
    if (queryId) params.append('query', queryId);
    const queryString = params.toString();
    return `${thirdFloorMapUrl}${queryString ? `?${queryString}` : ''}`;
  })()}
  className="w-full h-full border-0"
  title="1/F Library Map"
  key={`${facility}-${queryId}`}
/>
        </div>

        {/* right panel with search and categories (25% width) */}
        <div
  className={`${
    isPanelCollapsed ? "md:w-0 md:overflow-hidden" : "md:w-1/4"
  } w-full h-[40vh] md:h-[50vh] flex flex-col md:border-l border-t md:border-t-0 bg-white border-gray-300 transition-all duration-300 ease-in-out relative`}  >               {/* search input */}
          <div className="p-3 border-b border-gray-200">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg
                  className="w-4 h-4 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <input
                type="text"
                className="w-full px-8 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Search Location / Facility"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {facilitiesLoading && (
                <div className="p-2 bg-blue-50 border-b text-xs">
                  <div className="text-blue-600">Loading...</div>
                </div>
              )}

              {facilitiesError && (
                <div className="p-2 bg-red-50 border-b text-xs">
                  <div className="text-red-600">
                    ⚠️ API Error: Using fallback data
                  </div>
                </div>
              )}

              {!facilitiesLoading &&
                !facilitiesError &&
                apiFacilities.length > 0 && <div></div>}
              {searchTerm && (
                <button
                  className="absolute right-3 top-2 text-gray-400 hover:text-gray-600"
                  onClick={() => setSearchTerm("")}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* categories and subcategories(scrollable) */}
          <div className="overflow-y-auto flex-1">
            {filteredCategories.map((category, index) => (
              <div key={index} className="border-b-2 border-gray-200">
                <div
                  className="p-3 flex items-center justify-between cursor-pointer bg-teal-600 text-white"
                  onClick={() => toggleCategory(category.name)}
                >
                  <span className="font-medium text-sm">{category.name}</span>
                  {category.subcategories &&
                    category.subcategories.length > 0 && (
                      <svg
                        className={`w-4 h-4 transition-transform ${
                          expandedCategories[category.name]
                            ? "transform rotate-90"
                            : ""
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    )}
                </div>

                {/* subcategories */}
{expandedCategories[category.name] &&
  category.subcategories && (
    <div className="bg-gray-50">
      {(() => {
        // Get the API category to access section_title_l2
        const apiCategory = apiFacilities.find(cat => cat.name === category.name);
        
        // Group subcategories by section_title_l2 with proper typing
        const groupedSubcategories = category.subcategories.reduce((groups: Record<string, typeof category.subcategories>, subcategory) => {
          const apiSub = apiCategory?.subcategories.find(sub => sub.name === subcategory.name);
          const sectionTitle = apiSub?.section_title_l2 || "no-section";
          
          if (!groups[sectionTitle]) {
            groups[sectionTitle] = [];
          }
          groups[sectionTitle].push(subcategory);
          return groups;
        }, {} as Record<string, typeof category.subcategories>);

        // Render groups
        return Object.entries(groupedSubcategories).map(([sectionTitle, subcategories], groupIndex) => (
          <div key={groupIndex}>
            {/* Section Title Header - only if sectionTitle exists and isn't null/empty/no-section */}
            {sectionTitle && sectionTitle.trim() && sectionTitle !== "null" && sectionTitle !== "no-section" && (
              <div className="bg-gray-200 cursor-default p-2 pl-6 border-t border-gray-200">
                <div className="font-bold text-sm text-gray-800">
                  {sectionTitle}
                </div>
              </div>
            )}
            
            {/* Subcategories under this section */}
            {subcategories.map((subcategory, subIndex) => (
              <div key={subIndex}>
                <div
                  className="p-2 pl-6 border-t border-gray-200 bg-white hover:bg-gray-100 flex items-center justify-between cursor-pointer"
                  onClick={() => {
                    if (subcategory.subItems && subcategory.subItems.length > 0) {
                      toggleSubcategory(subcategory.name);
                    } else {
                      if (handleSpecialFacilities(subcategory.name)) {
                        return; // Exit early if this was a special facility
                      }
                      // Get API data for query_id
                      const apiCategory = apiFacilities.find(cat => cat.name === category.name);
                      const apiSub = apiCategory?.subcategories.find(sub => sub.name === subcategory.name);
                      
                      const facilityFloor =getFacilityFloor(apiSub?.query_id || mapCategoryToFacility(subcategory.name))
                      
                      if (facilityFloor && facilityFloor !== "3/F") {
                        const params = new URLSearchParams();
                        params.append('facility', encodeURIComponent(apiSub?.query_id || mapCategoryToFacility(subcategory.name)));
                        params.append('query', apiSub?.query_id || mapCategoryToFacility(subcategory.name)); 
                        params.append('from', '3/F');
                        if (isDisplayToggled) params.append('display', 'toggled');
                        
                        let floorRoute = "";
                        switch (facilityFloor) {
                          case "1/F":
                            floorRoute = "first";
                            break;
                          case "M/F":
                            floorRoute = "m";
                            break;
                          case "2/F":
                            floorRoute = "second";
                            break;
                          default:
                            floorRoute = "third";
                        }
                        
                        navigate(`/${floorRoute}-floor?${params.toString()}`);
                      } else {
                        setSpecialMapUrl(null);
                        const mappedFacility = mapCategoryToFacility(subcategory.name);
                        setFacility(mappedFacility);
                        setQueryId(apiSub?.query_id || "");
                        console.log("API Facility selected:", subcategory.name);
                        console.log("Query ID:", apiSub?.subItems?.[0]?.query_id);
                      }
                    }
                  }}
                >
                  <div>
                    <div className="font-medium text-xs">
                      {subcategory.name}
                    </div>
                  </div>
                  {subcategory.subItems && subcategory.subItems.length > 0 && (
                    <svg
                      className={`w-3 h-3 text-gray-700 transition-transform ${
                        expandedSubcategories[subcategory.name]
                          ? "transform rotate-90"
                          : ""
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  )}
                </div>

                {/* third level - sub-items */}
                {expandedSubcategories[subcategory.name] && subcategory.subItems && (
                  <div className="bg-gray-100">
                    {(() => {
                      // Group sub-items by section_title_l3 with proper typing
                      const apiCategory = apiFacilities.find(cat => cat.name === category.name);
                      const apiSub = apiCategory?.subcategories.find(sub => sub.name === subcategory.name);
                      
                      const groupedSubItems = subcategory.subItems.reduce((groups: Record<string, typeof subcategory.subItems>, subItem) => {
                        const apiSubItem = apiSub?.subItems?.find(item => item.name === subItem.name);
                        const sectionTitle = apiSubItem?.section_title_l3 || "no-section";
                        
                        if (!groups[sectionTitle]) {
                          groups[sectionTitle] = [];
                        }
                        groups[sectionTitle].push(subItem);
                        return groups;
                      }, {} as Record<string, typeof subcategory.subItems>);

                      return Object.entries(groupedSubItems).map(([sectionTitle, subItems], groupIndex) => (
                        <div key={groupIndex}>
                          {/* Section Title Header for third level */}
                          {sectionTitle && sectionTitle.trim() && sectionTitle !== "null" && sectionTitle !== "no-section" && (
                            <div className="bg-gray-300 cursor-default p-2 pl-10 border-t border-gray-200">
                              <div className="font-bold text-xs text-gray-800">
                                {sectionTitle}
                              </div>
                            </div>
                          )}
                          
                          {/* Sub-items under this section */}
                          {subItems.map((subItem, subItemIndex) => (
                            <div
                              key={subItemIndex}
                              className="p-2 pl-10 border-t border-gray-200 bg-white hover:bg-gray-200 flex items-center cursor-pointer"
                              onClick={() => {
                                if (handleSpecialFacilities(subItem.name)) {
                                  return; // Exit early if this was a special facility
                                }
                                const apiCategory = apiFacilities.find(cat => cat.name === category.name);
                                const apiSub = apiCategory?.subcategories.find(sub => sub.name === subcategory.name);
                                const apiSubItem = apiSub?.subItems?.find(item => item.name === subItem.name);
                                
                                const facilityFloor = getFacilityFloor(apiSubItem?.query_id || mapCategoryToFacility(subItem.name));
                                
                                if (facilityFloor && facilityFloor !== "3/F") {
                                  const params = new URLSearchParams();
                                  params.append('facility', encodeURIComponent(mapCategoryToFacility(subItem.name)));
                                  params.append('from', '3/F');
                                  if (apiSubItem?.query_id) params.append('query', apiSubItem.query_id);
                                  if (isDisplayToggled) params.append('display', 'toggled');

                                  
                                  let floorRoute = "";
                                  switch (facilityFloor) {
                                    case "1/F":
                                      floorRoute = "first";
                                      break;
                                    case "M/F":
                                      floorRoute = "m";
                                      break;
                                    case "2/F":
                                      floorRoute = "second";
                                      break;
                                    default:
                                      floorRoute = "third";
                                  }
                                  
                                  navigate(`/${floorRoute}-floor?${params.toString()}`);
                                } else {
                                  setSpecialMapUrl(null);
                                  const mappedFacility = mapCategoryToFacility(subItem.name);
                                  setFacility(mappedFacility);
                                  setQueryId(apiSubItem?.query_id || "");
                                  console.log("API Sub-item selected:", subItem.name);
                                  console.log("Query ID:", apiSubItem?.query_id);
                                }
                              }}
                            >
                              <div>
                                <div className="font-medium text-xs">
                                  {subItem.name}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ));
                    })()}
                  </div>
                )}
              </div>
            ))}
          </div>
        ));
      })()}
    </div>
  )}
              </div>
            ))}
          </div>
        </div>
         {/* Toggle tab - always visible */}
         <div
  className={`hidden md:block fixed ${
    isPanelCollapsed ? "right-0" : "right-[25%]"
  } top-[40%] transform -translate-y-1/2 bg-teal-600 text-white p-2 cursor-pointer shadow-lg rounded-l-lg z-30 hover:bg-teal-700 transition-all duration-300 ease-in-out`}
  onClick={() => setIsPanelCollapsed(!isPanelCollapsed)}
>
          <svg
            className={`w-6 h-6 transition-transform duration-300 ${
              isPanelCollapsed ? "transform rotate-180" : ""
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </div>
      </div>
 
     
  
    </div>
  );
};

export default ThirdFloorViewer;
